import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Film, Loader2, Sparkles, Play, Share2, Trash2, Clapperboard, Music2, Mic2, Volume2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Bits";
import { CinematicPlayer } from "@/components/CinematicPlayer";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FOCUS_PRESETS = [
  "the epic boss finale",
  "the hero's journey",
  "a mysterious teaser",
  "an action-packed montage",
];

const TEMPLATES = [
  { id: "trailer", label: "Reveal Trailer", emoji: "🎬", desc: "Punchy AAA reveal that builds to a climax", shots: 5 },
  { id: "teaser", label: "Teaser", emoji: "🌒", desc: "Short & mysterious — hints without revealing", shots: 3 },
  { id: "launch", label: "Launch", emoji: "🚀", desc: "Triumphant — celebrates the biggest moments", shots: 6 },
  { id: "speedrun", label: "Speedrun Hype", emoji: "⚡", desc: "Fast, high-energy montage cut", shots: 5 },
];

const MOODS = [
  { id: "", label: "Auto", emoji: "✨" },
  { id: "epic orchestral", label: "Epic", emoji: "🎺" },
  { id: "dark synth pulse", label: "Dark", emoji: "🌑" },
  { id: "battle action", label: "Action", emoji: "⚔️" },
  { id: "mysterious ambient", label: "Mystery", emoji: "🌫️" },
];

const NARRATORS = [
  { id: "epic", label: "Epic Announcer", emoji: "🎙️", voice: "onyx", speed: 0.96 },
  { id: "heroic", label: "Heroic", emoji: "🦸", voice: "fable", speed: 1.0 },
  { id: "mysterious", label: "Mysterious", emoji: "🌙", voice: "echo", speed: 0.9 },
  { id: "energetic", label: "Energetic", emoji: "⚡", voice: "nova", speed: 1.08 },
  { id: "warm", label: "Warm", emoji: "☀️", voice: "shimmer", speed: 1.0 },
];

const MOOD_TO_NARRATOR = {
  "": "epic",
  "epic orchestral": "heroic",
  "dark synth pulse": "mysterious",
  "battle action": "energetic",
  "mysterious ambient": "mysterious",
};

export default function CinematicStudio() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [focus, setFocus] = useState(FOCUS_PRESETS[0]);
  const [template, setTemplate] = useState("trailer");
  const [music, setMusic] = useState("");
  const [narrator, setNarrator] = useState("epic");
  const [narratorTouched, setNarratorTouched] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [autoDirecting, setAutoDirecting] = useState(false);
  const [selected, setSelected] = useState(null);
  const pollRef = useRef(null);
  const sampleRef = useRef(null);
  const [sampling, setSampling] = useState(null);

  const sampleVoice = (n) => {
    if (!sampleRef.current) sampleRef.current = new Audio();
    const a = sampleRef.current;
    try { a.pause(); } catch { /* ignore */ }
    setSampling(n.id);
    a.src = `${BACKEND}/api/cinematic/voice-sample/${n.voice}.mp3`;
    const clear = () => setSampling((s) => (s === n.id ? null : s));
    a.onplaying = clear;
    a.onended = clear;
    a.onerror = () => { clear(); toast.error("Couldn't play that sample."); };
    a.play().catch(() => clear());
  };

  const chooseMusic = (id) => {
    setMusic(id);
    if (!narratorTouched) setNarrator(MOOD_TO_NARRATOR[id] || "epic");
  };
  const pickNarrator = (id) => { setNarrator(id); setNarratorTouched(true); };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/cinematic/${gameId}`);
      setItems(data?.cinematics || []);
    } catch (e) { toast.error("Couldn't open Cinematic Studio."); }
    finally { setLoading(false); }
  }, [gameId]);

  useEffect(() => { load(); }, [load]);

  // Poll while any trailer is still rendering.
  useEffect(() => {
    const anyProcessing = items.some((c) => c.status === "processing");
    if (anyProcessing) {
      pollRef.current = setInterval(load, 5000);
      return () => clearInterval(pollRef.current);
    }
  }, [items, load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const nar = NARRATORS.find((n) => n.id === narrator) || NARRATORS[0];
      const { data } = await api.post(`/cinematic/${gameId}/generate`, { focus: focus.trim(), template, music_mood: music, voice: nar.voice, voice_speed: nar.speed }, { timeout: 40000 });
      toast.success("🎬 Rolling! Your trailer is rendering — it'll appear below in ~1 minute.");
      setItems((l) => [data, ...l]);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" ? d : formatApiError(d)) || "Couldn't start the trailer.");
    } finally { setGenerating(false); }
  };

  // One-tap Auto-Director: ask the backend for the best recipe, apply it, and roll immediately.
  const autoDirect = async () => {
    setAutoDirecting(true);
    try {
      const { data } = await api.get(`/cinematic/${gameId}/suggest`);
      const nextTpl = data.template || template;
      const nextMusic = typeof data.music_mood === "string" ? data.music_mood : music;
      const nextNarrator = data.narrator || narrator;
      const nextFocus = (data.focus || focus).trim();
      setTemplate(nextTpl); setMusic(nextMusic); setNarrator(nextNarrator); setNarratorTouched(true); setFocus(nextFocus);
      const nar = NARRATORS.find((n) => n.id === nextNarrator) || NARRATORS[0];
      const { data: cine } = await api.post(`/cinematic/${gameId}/generate`, { focus: nextFocus, template: nextTpl, music_mood: nextMusic, voice: nar.voice, voice_speed: nar.speed }, { timeout: 40000 });
      toast.success(`✨ ${data.why || "Auto-directed"} — rendering now.`);
      setItems((l) => [cine, ...l]);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" ? d : formatApiError(d)) || "Couldn't auto-direct the trailer.");
    } finally { setAutoDirecting(false); }
  };

  const shareLink = (slug) => `${process.env.REACT_APP_BACKEND_URL}/api/cinematic/share/${slug}`;
  const copyShare = (slug) => {
    navigator.clipboard.writeText(shareLink(slug)).then(
      () => toast.success("🔗 Share link copied!"),
      () => toast.error("Couldn't copy — link: " + shareLink(slug)),
    );
  };

  const remove = async (c) => {
    setItems((l) => l.filter((x) => x.id !== c.id));
    if (selected?.id === c.id) setSelected(null);
    try { await api.delete(`/cinematic/item/${c.id}`); } catch { /* optimistic */ }
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Spinner /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="cinematic-studio">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/app/build/${gameId}`)} data-testid="cine-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-bold flex items-center gap-2 truncate"><Clapperboard className="w-6 h-6 text-rose-500" /> AI Cinematic Studio</h1>
            <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">Turn your game into a shareable, auto-playing trailer</p>
          </div>
        </div>
        <Button onClick={autoDirect} disabled={autoDirecting || generating} data-testid="cine-auto-direct-btn"
          className="rounded-full bg-gradient-to-r from-fuchsia-500 to-amber-400 hover:opacity-90 text-black font-bold shrink-0">
          {autoDirecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {autoDirecting ? "Directing…" : "Auto-direct"}
        </Button>
      </div>

      {/* Generator */}
      <div className="rounded-2xl border border-rose-500/40 bg-gradient-to-br from-rose-500/10 to-amber-500/5 p-4 mb-6">
        <p className="text-[11px] font-bold uppercase tracking-wide text-rose-600 dark:text-rose-400 mb-2 flex items-center gap-1.5"><Film className="w-3.5 h-3.5" /> Direct a new trailer</p>
        <p className="text-[11px] font-bold uppercase tracking-wide text-rose-600 dark:text-rose-400 mb-2">Pick a style</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="cine-templates">
          {TEMPLATES.map((t) => (
            <button key={t.id} onClick={() => setTemplate(t.id)} data-testid={`cine-template-${t.id}`}
              className={`text-left rounded-xl border p-3 transition-colors ${template === t.id ? "border-rose-500 bg-rose-500/10 ring-1 ring-rose-500/40" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>
              <div className="text-lg leading-none mb-1">{t.emoji}</div>
              <div className="text-xs font-bold">{t.label}</div>
              <div className="text-[10px] text-[hsl(var(--muted-foreground))] leading-snug mt-0.5">{t.desc}</div>
              <div className="text-[10px] text-rose-500/80 font-semibold mt-1">{t.shots} shots</div>
            </button>
          ))}
        </div>
        <p className="text-[11px] font-bold uppercase tracking-wide text-rose-600 dark:text-rose-400 mb-2">Focus</p>
        <div className="flex flex-wrap gap-2 mb-3" data-testid="cine-focus-presets">
          {FOCUS_PRESETS.map((f) => (
            <button key={f} onClick={() => setFocus(f)} data-testid={`cine-focus-${f.replace(/\s+/g, "-")}`}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${focus === f ? "border-rose-500 bg-rose-500/10 text-rose-500" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>{f}</button>
          ))}
        </div>
        <p className="text-[11px] font-bold uppercase tracking-wide text-rose-600 dark:text-rose-400 mb-2 flex items-center gap-1.5"><Music2 className="w-3.5 h-3.5" /> Music mood</p>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="cine-music-moods">
          {MOODS.map((m) => (
            <button key={m.id || "auto"} onClick={() => chooseMusic(m.id)} data-testid={`cine-music-${m.id ? m.id.split(" ")[0] : "auto"}`}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors flex items-center gap-1 ${music === m.id ? "border-rose-500 bg-rose-500/10 text-rose-500" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>
              <span>{m.emoji}</span> {m.label}
            </button>
          ))}
        </div>
        <p className="text-[11px] font-bold uppercase tracking-wide text-rose-600 dark:text-rose-400 mb-2 flex items-center gap-1.5"><Mic2 className="w-3.5 h-3.5" /> Narrator voice <span className="normal-case font-normal text-[10px] text-amber-500">✨ auto-matched to your music</span></p>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="cine-narrators">
          {NARRATORS.map((n) => {
            const active = narrator === n.id;
            const suggested = (MOOD_TO_NARRATOR[music] || "epic") === n.id;
            return (
              <div key={n.id} className={`flex items-center rounded-full border overflow-hidden text-xs font-semibold transition-colors ${active ? "border-rose-500 bg-rose-500/10 text-rose-500" : "border-[hsl(var(--border))]"}`}>
                <button onClick={() => pickNarrator(n.id)} data-testid={`cine-narrator-${n.id}`} className="pl-3 pr-2 py-1.5 flex items-center gap-1 hover:bg-[hsl(var(--secondary))]/40">
                  <span>{n.emoji}</span> {n.label}
                  {suggested && <span title="Suggested for your music mood" className="text-amber-400" data-testid={`cine-narrator-suggested-${n.id}`}>✨</span>}
                </button>
                <button onClick={() => sampleVoice(n)} title="Hear a sample" data-testid={`cine-narrator-preview-${n.id}`} className="px-2 py-1.5 border-l border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]/40">
                  {sampling === n.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Volume2 className="w-3 h-3" />}
                </button>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <input value={focus} onChange={(e) => setFocus(e.target.value)} maxLength={120} placeholder="What should the trailer be about?" data-testid="cine-focus-input"
            className="flex-1 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-2.5 text-sm" />
          <Button className="rounded-full" onClick={generate} disabled={generating || !focus.trim()} data-testid="cine-generate-btn">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {generating ? "Rolling…" : "Generate trailer"}
          </Button>
        </div>
        <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2">Uses 25 AI credits · AI writes the script, paints keyframes, and records a voiceover (~1 min). Clearly labeled AI-generated.</p>
      </div>

      {selected && (
        <div className="mb-6" data-testid="cine-selected-player">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-display text-lg font-bold">{selected.title}</h2>
            <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>Close</Button>
          </div>
          <CinematicPlayer cinematic={selected} autoplay />
        </div>
      )}

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-10 text-center" data-testid="cine-empty">
          <Clapperboard className="w-10 h-10 mx-auto text-[hsl(var(--muted-foreground))] mb-3" />
          <p className="text-sm text-[hsl(var(--muted-foreground))]">No trailers yet — pick a focus above and hit <b>Generate trailer</b> to make your first shareable cinematic.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="cine-list">
          {items.map((c) => {
            const cover = (c.shots || []).find((s) => s.image_url)?.image_url;
            const processing = c.status === "processing";
            const failed = c.status === "failed";
            return (
              <div key={c.id} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden" data-testid={`cine-item-${c.id}`}>
                <button onClick={() => !processing && !failed && setSelected(c)} className="relative block w-full aspect-video bg-black/60 grid place-items-center" disabled={processing || failed}>
                  {cover && !processing ? (
                    <img src={cover.startsWith("http") ? cover : `${process.env.REACT_APP_BACKEND_URL}${cover}`} alt={c.title} className="w-full h-full object-cover" />
                  ) : null}
                  {processing && <div className="flex flex-col items-center gap-2 text-white/80"><Loader2 className="w-7 h-7 animate-spin" /><span className="text-xs">Rendering your trailer…</span></div>}
                  {failed && <span className="text-xs text-rose-300">Render failed — try again</span>}
                  {!processing && !failed && <span className="absolute inset-0 grid place-items-center bg-black/25"><span className="w-14 h-14 grid place-items-center rounded-full bg-white/90 text-black"><Play className="w-6 h-6 ml-0.5" /></span></span>}
                </button>
                <div className="p-3">
                  <p className="font-display font-bold truncate">{c.title}</p>
                  {c.tagline && <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">{c.tagline}</p>}
                  <div className="flex items-center gap-2 mt-3">
                    <Button size="sm" variant="outline" className="rounded-full flex-1" disabled={processing || failed} onClick={() => copyShare(c.slug)} data-testid={`cine-share-${c.id}`}><Share2 className="w-4 h-4" /> Share</Button>
                    <Button size="icon" variant="ghost" className="h-9 w-9 text-[hsl(var(--muted-foreground))]" onClick={() => remove(c)} data-testid={`cine-delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
